import { useState, useEffect } from "react";
import InputNew from "components/Form/newAuthInput";
import PhoneInput from "components/atoms/PhoneInput/newAuthPhoneInput";
import Button from "components/Form/Button";
import { useLang } from "hooks/useLang";
import useLogin from "hooks/useLogin";
import { AuthModalSteps } from "./AuthModal";

interface LoginProps {
  onSwitchToSignup: () => void;
  setStep: (step: AuthModalSteps) => void;
  setAuthData: (data: any) => void;
  setAuthMode: (mode: "login" | "signup") => void;
  step: AuthModalSteps;
}

const AuthModalLogin = ({
  onSwitchToSignup,
  setStep,
  setAuthData,
  setAuthMode,
  step,
}: LoginProps) => {
  const { t } = useLang();
  const {
    submit,
    handleInput,
    disableButton,
    form,
    inputType,
    inputValue,
    check,
    errorForm,
    isLoading,
    resetUserExist,
  } = useLogin({});

  const [loginType, setLoginType] = useState<"email" | "phone">("phone");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    resetUserExist();
  }, [loginType]);

  useEffect(() => {
    if (step === "login") {
      resetUserExist();
      setHasSubmitted(false);
    }
  }, [step]);

  useEffect(() => {
    if (check.isUserExist && hasSubmitted) {
      setAuthData({
        inputValue,
        inputType,
        callingCode: form.callingCode,
        email: form.email.toLowerCase(),
      });
      setStep("ver");
      setAuthMode("login");
    }
  }, [
    check.isUserExist,
    inputValue,
    inputType,
    form.callingCode,
    setAuthData,
    setStep,
    setAuthMode,
    hasSubmitted,
  ]);

  const toggleLoginType = (type: "phone" | "email") => {
    setLoginType(type);
    handleInput("email", "");
    handleInput("phone", "");
    handleInput("password", "");
  };

  const handleSignupInsteadClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    onSwitchToSignup();
    setAuthMode("signup");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await submit(e);
      setHasSubmitted(true);
    } catch (error) {
      console.error("Login submission error: ", error);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="w-full">
        <div className="w-full flex flex-row bg-[#FFFFFF4D] justify-between rounded-full">
          <div
            onClick={() => toggleLoginType("phone")}
            className={`cursor-pointer flex-1 rounded-full text-center py-2 ${
              loginType === "phone" ? "bg-[#fff]" : ""
            }`}
          >
            <span
              className={`${
                loginType === "phone"
                  ? "text-[#221953]"
                  : "text-black dark:text-white"
              }`}
            >
              {t("phone")}
            </span>
          </div>
          <div
            onClick={() => toggleLoginType("email")}
            className={`cursor-pointer flex-1 rounded-full text-center py-2 ${
              loginType === "email" ? "bg-[#fff]" : ""
            }`}
          >
            <span
              className={`${
                loginType === "email"
                  ? "text-[#221953]"
                  : "text-black dark:text-white"
              }`}
            >
              {t("email")}
            </span>
          </div>
        </div>
        <div className="mt-4 px-3">
          <span className="text-4 font-400 text-black dark:text-white">
            {t(`enter${loginType}ToLogin`)}
          </span>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 md:gap-5">
        {loginType === "email" ? (
          <InputNew
            label={t("lb_email")}
            inputID="email_login"
            name="email"
            type="email"
            autoComplete="email"
            onChange={(e) => handleInput("email", e.target.value)}
            value={form.email}
            error={
              errorForm?.email === "emailNotExisted" ? (
                <>
                  {t("emailNotExisted")}. {t("dont_have_account")}{" "}
                  <a
                    onClick={handleSignupInsteadClick}
                    href="#"
                    className="text-[#0DB9A0]"
                  >
                    {t("btn_signup")}
                  </a>
                </>
              ) : errorForm?.email ? (
                t(errorForm.email)
              ) : null
            }
            autoFocus
            placeholder={t("lib_email")}
            inputContainerClassName="h-[40px]"
          />
        ) : (
          <PhoneInput
            callingCode={form.callingCode}
            setCallingCode={(callingCode: string) =>
              handleInput("callingCode", callingCode)
            }
            setPhone={(phone: string) => handleInput("phone", phone)}
            phone={form.phone}
            error={
              errorForm?.phone === "phoneNotExisted" ? (
                <>
                  {t("phoneNotExisted")}. {t("dont_have_account")}{" "}
                  <a
                    onClick={handleSignupInsteadClick}
                    href="#"
                    className="text-[#0DB9A0]"
                  >
                    {t("btn_signup")}
                  </a>
                </>
              ) : errorForm?.phone ? (
                t(errorForm.phone)
              ) : null
            }
            inputContainerClassName="h-[40px]"
            autoFocus
            placeholder={t("phoneNumber")}
          />
        )}

        <div className="w-full bg-[#F0F0F033] h-[1px] mt-3"></div>

        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <p className="text-sm text-gray-600 dark:text-gray-200 md:text-lg">
              {t("dont_have_account")}
            </p>
            <a
              onClick={handleSignupInsteadClick}
              href="#"
              className="text-[#0DB9A0] text-sm md:text-lg"
            >
              {t("btn_signup")}
            </a>
          </div>

          <Button
            text={t("btn_Continue")}
            loading={check.isLoading || isLoading}
            disabled={disableButton()}
            type="submit"
            theme="modern"
            className="p-2 bg-[#1DC2AB] w-52"
          />
        </div>
      </form>
    </div>
  );
};

export default AuthModalLogin;
